<script>
import { computed, inject, nextTick, onMounted, reactive, ref, watch } from "vue";
import _head from "lodash/head";
import debounce from "lodash/debounce";
import moment from "moment";

import Logout from "@/components/Logout.vue";
import Brand from "@/components/Brand.vue";
import ModalPrescriptionDetails from "@/components/PrescriptionDetails";
import PrescriptionRejected from "@/components/PrescriptionRejected.vue";
import Empty from "@/components/Modal/Empty";
import SpecialActions from "@/components/SpecialActions";

import { hideModal } from "@/use/modal/hide";
import { update as updateState } from '@/use/repositories/testWorkQueue/update';
import { store as storeTestWorkQueue } from "@/use/repositories/testWorkQueue/store";
import { error } from "@/use/toast/error";
import { storeAsRejected } from "@/use/repositories/prescriptionTestRejected/storeAsRejected";
import { fetchRejectedReasons } from "@/use/repositories/prescriptionTestRejected/fetchRejectedReasons";
import { success } from "@/use/toast/success";
import { dateFormatter } from "@/use/utilities/time/dateFormatter";
import { useI18n } from "vue-i18n";
import { fetchPrescriptionTest } from "@/use/repositories/prescriptions/fetchPrescriptionTest";
import { fetchById as validatePrescriptionTest } from "@/use/repositories/prescriptionTests/fetchById";
import { index as fetchAllTesWorkQueue } from '@/use/repositories/testWorkQueue/index';
import { print } from "@/use/repositories/devices/print";
import { update as updateDevice } from "@/use/repositories/devices/update";
import { show as validateDevice } from "@/use/repositories/devices/show";
import { index as fetchDevices } from "@/use/repositories/devices/index";
import { show as showCustomer } from "@/use/repositories/customer/show";
import { calculateDeliveryDate } from "@/use/repositories/clinic-deliveries/calculateDeliveryDate";
import { updateDeliveryDate } from "@/use/repositories/clinic-deliveries/updateDeliveryDate";
import { index as finishedByCustomerClinic } from "@/use/repositories/dates/index";

import BatteryAlert from "@/components/devices/BatteryAlert.vue";
import Toggle from '@vueform/toggle';
import { DatePicker } from 'v-calendar';
import Standard from '@/components/Modal/Standard.vue';

export default {
  name: "Home",
  methods: { dateFormatter },
  components: {
    PrescriptionRejected,
    Logout,
    Brand,
    ModalPrescriptionDetails,
    Empty,
    SpecialActions,
    BatteryAlert,
    Toggle,
    DatePicker,
    Standard
},
  setup() {
    const { devicesType, departments } = inject("constants");
    const i18n = useI18n();
    const barCodeInput = ref("");
    const deviceId = ref(null);
    const testWorkQueueIdSelected = ref();
    const testWorkQueues = ref([]);
    const prescriptionsInQueue = ref([]);
    const clearRejectedInfo = ref(false);
    const prescriptionRejectedReasonsId = ref([]);
    const prescriptionRejectedReasons = ref([]);
    const currentPrescription = ref();
    const checkinCompleted = ref(false);

    const displayRules = reactive({
      showStoreRejectedModal: false,
      showBatteryAlert: false,
      deviceInputDisabled: false,
      disableBarCodeInput: false,
      disabledCheckIn: false,
    })

    const currentCustomer = reactive({
      customer_id: null,
      delivery_clinic_id: null,
    });

    const disabledInput = computed(() => {
      return displayRules.disabledCheckIn && ! prescriptionRejectedReasonsId.value.length;
    });

    const initScanFlow = debounce(async () => {
      if (barCodeInput.value.trim() === "") return;

      if (barCodeInput.value.length === 4 && ! displayRules.deviceInputDisabled) validateDigitalLabel();
      else {
        if (checkinCompleted.value) {
          prescriptionsInQueue.value = [];
          checkinCompleted.value = false;
        }
        validatePrescription()
      }
    }, 1000);

    const validatePrescription = async () => {
      try {
        const response = await fetchPrescriptionTest(barCodeInput.value.trim(), "with_valid_prescription_test");
        currentPrescription.value = response.data;

        if (prescriptionRejectedReasonsId.value.length) {
          await rejectPrescription(currentPrescription.value);
          return;
        }

        if (isSameCustomer(currentPrescription.value)) {
          return error(i18n.t("You cannot associate prescription from different customers to the same tag"))
        }

        const validationResponse = await validatePrescriptionTest(
          currentPrescription.value.current_test.id,
          {
            action: "validate_checkin",
            is_digital: 0,
          }
        );

        await completeValidationAndShowCustomer(validationResponse);

        if (displayRules.deviceInputDisabled && ! displayRules.showCustomerExistFlow) {

          await completeCheckin();
        }
      } catch (err) {
        handleError(err);
      } finally {
        clearAndSetFocusInput();
      }
    };

    const showDateModal = ref(false);
    const selectedDate = ref(new Date());
    let resolveDateModal = null;

    const showConfirmDateModal = ref(false);
    const pendingDateToConfirm = ref(null);
    let resolveConfirmDateModal = null;

    const askUserForDate = () => {
      return new Promise((resolve) => {
        selectedDate.value = null;
        showDateModal.value = true;
        resolveDateModal = resolve;
      });
    };
    const confirmDate = () => {
      showDateModal.value = false;
      hideModal('manualDateModal');
      if (resolveDateModal) {
        resolveDateModal(selectedDate.value);
        resolveDateModal = null;
      }
    };

    const askUserToConfirmDate = (date) => {
      return new Promise((resolve) => {
        pendingDateToConfirm.value = date;
        showConfirmDateModal.value = true;
        resolveConfirmDateModal = resolve;
      });
    };
    const confirmPendingDate = () => {
      showConfirmDateModal.value = false;
      hideModal('confirmDateModal');
      if (resolveConfirmDateModal) {
        resolveConfirmDateModal(pendingDateToConfirm.value);
        resolveConfirmDateModal = null;
      }
    };
    const chooseAnotherDate = () => {
      showConfirmDateModal.value = false;
      askUserForDate().then((newDate) => {
        if (resolveConfirmDateModal) {
          resolveConfirmDateModal(newDate);
          resolveConfirmDateModal = null;
        }
      });
    };

    const fetchPreviousAppointmentDate = async ({ customerId, clinicId }) => {
      try {
        const response = await finishedByCustomerClinic({
          action: "with_finished_by_customer_clinic",
          customer_id: customerId,
          clinic_id: clinicId
        });
        const lastDate = _head(response.dates);
        if (!lastDate?.start_at) return null;
        return moment(lastDate.start_at, "YYYY-MM-DD HH:mm:ss").toISOString();
      } catch (fetchError) {
        console.error("Errore nel recupero della data appuntamento precedente:", fetchError);
        return null;
      }
    };

    const parsePrescriptionData = async (testWorkQueueData, prescriptionTest) => {
      const { prescription } = prescriptionTest;
      const { delivery_clinic: clinic, doctor, customer } = prescription;


      let deliveryDate = null;
      let labelExplanation = null;
      try {
        const formattedAppointmentDate = prescriptionTest?.date?.start_at ?
          dateFormatter(prescriptionTest.date.start_at, { dash: true, iso: true }) : null;


        const deliveryDateResult = await calculateDeliveryDate({
          clinicId: clinic.id,
          appointmentDate: formattedAppointmentDate,
        });
        if (deliveryDateResult?.date && deliveryDateResult?.time) {

          const backendDate = `${deliveryDateResult.date} ${deliveryDateResult.time}`;
          const backendDateObj = new Date(`${deliveryDateResult.date}T${deliveryDateResult.time}`);
          const now = new Date();
          const twoHoursLater = new Date(now.getTime() + 2 * 60 * 60 * 1000);
          const pad = n => n.toString().padStart(2, '0');
          const formatDate = (d) => {
            const y = d.getFullYear();
            const m = pad(d.getMonth() + 1);
            const dd = pad(d.getDate());
            const h = pad(d.getHours());
            const min = pad(d.getMinutes());
            const s = pad(d.getSeconds());
            return `${y}-${m}-${dd} ${h}:${min}:${s}`;
          };
          if (backendDateObj < now) {
            const userDate = await askUserForDate();
            if (!userDate) {
              error('Operazione annullata.');
              return null;
            }
            if (new Date(userDate) < now) {
              error('Data non valida. Operazione annullata.');
              return null;
            }
            deliveryDate = formatDate(userDate);
          } else if (backendDateObj >= now && backendDateObj <= twoHoursLater) {
            const userDate = await askUserToConfirmDate(backendDateObj);
            if (!userDate) {
              error('Data non valida. Operazione annullata.');
              return null;
            }
            deliveryDate = formatDate(userDate);
          } else {
            deliveryDate = backendDate;
            if (deliveryDateResult?.explanation) {
              labelExplanation = deliveryDateResult.explanation;
            }
          }
        } else {
          throw new Error('Nessuna data etichetta restituita');
        }

        if (deliveryDate) {
          try {
            await updateDeliveryDate({
              testWorkQueueId: testWorkQueueData.id,
              deliveryDate: deliveryDate,
            });
          } catch (updateError) {
            console.error('Errore nell\'aggiornamento della data di consegna:', updateError);
          }
        }
      } catch (e) {
        error('Errore nel calcolo della data etichetta.');
        return null;
      }
      const previousAppointmentDate = await fetchPreviousAppointmentDate({
        customerId: customer.id,
        clinicId: clinic.id
      });

      const result = {
        prescription, clinic, doctor, customer,
        prescription_test: prescriptionTest,
        deliveryDate,
        labelExplanation,
        selectedFork: _head(testWorkQueueData.test_work_queue_phases),
        testWorkQueueId: testWorkQueueData.id,
        previousAppointmentDate
      };
      return result;
    };

    const completeValidationAndShowCustomer = async (validationResponse) => {

      await approveWork(validationResponse.prescriptionTest);

      const { customer } = await showCustomer(_head(prescriptionsInQueue.value).customer.id, 'with_devices');

      if (customer.devices.length) setDeviceAndShowToUser(_head(customer.devices));
    }

    const approveWork = async (prescriptionTest) => {
      try {
        const response = await storeTestWorkQueue(prescriptionTest.id, "analog_check_in");
        const { testWorkQueue } = response;
        const testWorkQueueData = testWorkQueue.data;
        await queuePrescriptionForCustomer(testWorkQueueData, prescriptionTest);
      } catch (err) {
        handleError(err);
      }
    };

    const queuePrescriptionForCustomer = async (testWorkQueueData, prescriptionTest) => {
      const parsedData = await parsePrescriptionData(testWorkQueueData, prescriptionTest);
      if (!parsedData) return;
      addToQueue(parsedData);
      currentCustomer.customer_id = prescriptionTest.prescription.customer.id;
      currentCustomer.delivery_clinic_id = prescriptionTest.prescription.delivery_clinic.id
    }

    const printLabel = async (queueIds) => {
      try {
        const response = await fetchAllTesWorkQueue({action: 'with_base_64_labels', ids: queueIds}, true);

        if (response.testWorkQueue.length) {
          for (const testWorkQueue of response.testWorkQueue) {
            await print({
              device_type_slug: devicesType.ANALOG_LABEL,
              department_slug: departments.LOGISTICS,
              data: testWorkQueue.raw_data,
            });
          }
        }
      } catch (err) {
        handleError(err);
      }
    };

    const handleError = async(err) => {
      const showError = (message) => {
        error(message);
      }
      if (Array.isArray(err)) {
        for (const message of err) {
          showError(message)
        }
      } else {
        showError(err)
      }
    };

    const setDeviceAndShowToUser = (device) => {
      deviceId.value = device.id;
      displayRules.showCustomerExistFlow = true;
    }

    const validateDigitalLabel = async () => {

      displayRules.showBatteryAlert = false;

      if (! prescriptionsInQueue.value.length) {
        return error(i18n.t("Scan or insert a prescription before!"));
      }

      try {
        const response = await fetchDevices("by_name", barCodeInput.value, "name_term");
        if (! response.data.length) return error(i18n.t("Tag not found, try again"));

        const id = _head(response.data).id;

        const { device } = await validateDevice(id, currentCustomer.customer_id);

        if (device.extra_data?.battery !== "ok") {
          return displayRules.showBatteryAlert = true;
        }

        deviceId.value = device?.id;

        if (deviceId.value) {
          await completeCheckin();
        }
      } catch (err) {
        handleError(err);
      }
    }

    const rejectAndComplete = async () => {
      deviceId.value = null;
      displayRules.showCustomerExistFlow = false;
      await completeCheckin();
    }

    const updateRequestAtForQueuedPrescriptions = async () => {
      const updates = prescriptionsInQueue.value
        .filter(({ previousAppointmentDate }) => Boolean(previousAppointmentDate))
        .map(async ({ testWorkQueueId, previousAppointmentDate }) => {
          try {
            await updateState({
              id: testWorkQueueId,
              action: "update_request_at",
              request_at: previousAppointmentDate,
            });
          } catch (err) {
            console.error(`Errore nell'aggiornamento di request_at per TWQ ${testWorkQueueId}:`, err);
          }
        });

      await Promise.all(updates);
    };

    const completeCheckin = async () => {

      await updateRequestAtForQueuedPrescriptions();

      if (! displayRules.deviceInputDisabled && deviceId.value) {
        await updateDevice(deviceId.value, {
          action: "attach_prescription_tests",
          prescription_test_ids: prescriptionsInQueue.value.map(({ prescription_test }) => prescription_test.id),
        });
      }

      await printLabel(prescriptionsInQueue.value.map(({ testWorkQueueId }) => testWorkQueueId));

      success(i18n.t("Barcodes print successfully!"));

      resetSession()
      checkinCompleted.value = true;
      clearAndSetFocusInput();
    }

    const resetSession = () => {
      for (let key in displayRules) {
        if (key !== "deviceInputDisabled") displayRules[key] = false;
      }
      for (let key in currentCustomer) currentCustomer[key] = null;
      deviceId.value = [];
    }

    const clearAndSetFocusInput = async () => {
      barCodeInput.value = "";
      await nextTick();
      const input = document.getElementById("scanBarcode");
      if (input !== null) input.focus();
    };

    const syncTesWorkQueues = async (id) => {
      let index = testWorkQueues.value.findIndex((twq) => twq.id === id);
      if (index !== -1) {
        testWorkQueues.value.splice(index, 1);
      }
      testWorkQueueIdSelected.value = null;
    };

    const formattedDate = (value) => {
      return moment(value).format("YYYY-MM-DD hh:mm");
    };

    const addToQueue = (prescriptionTest) => {
      prescriptionsInQueue.value.push(prescriptionTest);
    }

    const hideStoreRejectedModal = () => {
      hideModal("storeRejectedModal");
      displayRules.showStoreRejectedModal = false;
      prescriptionRejectedReasonsId.value = [];
    };

    const rejectPrescription = async (prescriptionTest, existing = null) => {
      const reasonSlug = prescriptionRejectedReasons.value.find((reason) => reason.value == _head(prescriptionRejectedReasonsId.value)).slug;
      storeAsRejected(prescriptionTest.id.toString(), 'sent', reasonSlug)
        .then(() => {
          clearRejectedInfo.value = true;
          displayRules.disabledCheckIn = false;
          prescriptionRejectedReasonsId.value = [];
          if (existing) {
            updateState({
              id: testWorkQueueIdSelected.value,
              state_slug: "non-complient",
              action: "update_state"
            });
            syncTesWorkQueues(testWorkQueueIdSelected.value);
            hideModal('storeRejectedModal');
          }
          error(i18n.t("Prescription rejected, communication sent via chat"));
        })
        .finally(() => {
          clearAndSetFocusInput();
        });
    };

    const setMotivationIdSelected = (motivationId) => {
      prescriptionRejectedReasonsId.value = [motivationId];
    }

    const isSameCustomer = (prescription) => {
      return (
        currentCustomer.customer_id !== null &&
        currentCustomer.delivery_clinic_id !== null &&
        currentCustomer.customer_id !== prescription.customer_id
      );
    };

    onMounted(() => {
      fetchRejectedReasons().then((response) => {
        prescriptionRejectedReasons.value = response.rejectedReasons;
      });
      clearAndSetFocusInput();
    });

    watch(
      () => prescriptionRejectedReasonsId.value.length,
      (value) => {
        if (value > 0) clearAndSetFocusInput();
      }
    );

    watch(
      () => displayRules.disabledCheckIn,
      (value) => {
        if (!value) clearAndSetFocusInput();
      }
    );

    return {
      barCodeInput,
      formattedDate,
      initScanFlow,
      prescriptionRejectedReasonsId,
      disabledInput,
      clearRejectedInfo,
      rejectPrescription,
      prescriptionRejectedReasons,
      hideStoreRejectedModal,
      hideModal,
      setMotivationIdSelected,
      completeCheckin,
      prescriptionsInQueue,
      displayRules,
      deviceId,
      rejectAndComplete,
      showDateModal,
      selectedDate,
      confirmDate,
      showConfirmDateModal,
      pendingDateToConfirm,
      confirmPendingDate,
      chooseAnotherDate
    };
  },
};
</script>

<template>
  <div class="d-flex justify-content-between w-50">
    <Logout />
    <Brand :width="120" :height="120" />
  </div>
  <div>
    <div class="alert alert-primary d-flealign-items-center m-3 px-4 rounded-custom" role="alert">
      <li>{{ $t('Scan the prescription barcode first and then the Tag.') }}</li>
      <li>{{ $t('It is possible to associate the prescription with an existing Tag if the patient is already present in the laboratory.') }}</li>
      <li>{{ $t('Only if no Tag is available, you can proceed without association.') }}</li>
    </div>
  </div>

  <div class="container d-flex flex-wrap">
    <div class="col-sm-12 col-md-6 p-1"  :class="{ 'm-auto': ! prescriptionsInQueue.length }">
    <BatteryAlert v-if="displayRules.showBatteryAlert" class="m-auto mb-2 border-danger" />
      <div class=" card rounded-custom shadow border-0 p-4">
        <div class="card-body">
          <h3 class="mb-4 fw-normal text-center">{{ $t('Check-in') }}</h3>
          <PrescriptionRejected
            v-if="! prescriptionsInQueue.length"
            :clear="clearRejectedInfo"
            :prescription-rejected-reasons="prescriptionRejectedReasons"
            @enabled="value => displayRules.disabledCheckIn = value"
            @rejected-reason-ids-selected="id => prescriptionRejectedReasonsId = id" />
          <div class="mb-3">

            <div class="input-group mb-3">
              <span class="input-group-text">
                <img src="@/assets/icons/logistic-checkin/scanner-barcode.svg" width="30" alt="scanner-barcode" />
              </span>
              <input type="text" id="scanBarcode" autocomplete="off" class="form-control"
                :placeholder="$t('Scan the barcode')" v-model="barCodeInput" :disabled="disabledInput" @input="initScanFlow"/>
            </div>

            <div v-if="! displayRules.disabledCheckIn" class="mb-3">
              <Toggle v-model="displayRules.deviceInputDisabled" />
              <label class="ms-2 fw-bold">{{ $t('Tag association') }} {{ displayRules.deviceInputDisabled ? $t('not active') : $t('active') }}</label>
            </div>
          </div>
          <div class="">
          </div>
          <template v-if="displayRules.showCustomerExistFlow" >
            <h5 class="card-header rounded-custom d-flex">
              <img class="me-3" src="@/assets/icons/danger.svg" width="35" />
              {{ $t('The patient') }} {{ prescriptionsInQueue[0].customer.first_name}} {{ prescriptionsInQueue[0].customer.last_name}} {{ $t('is already present in the laboratory.') }}
            </h5>
            <div class="card-body">
              <p class="card-text">{{ $t('Associate this prescription with the existing Tag?') }}</p>
              <div class="d-flex justify-content-around">
                <button @click="completeCheckin" href="#" class="btn btn-violet">{{ $t('Confirm') }}</button>
                <button @click="rejectAndComplete" href="#" class="btn btn btn-outline-violet">{{ $t('Reject') }}</button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div v-if="prescriptionsInQueue.length" class="col-sm-12 col-md-6">
      <ul class="list-group p-1 list-unstyled">
        <li v-for="prescription in prescriptionsInQueue" :key="prescription" >
          <ModalPrescriptionDetails :data="prescription"/>
          <div v-if="prescription.labelExplanation" class="alert alert-info mt-2 mb-3">
            <div class="d-flex align-items-center">
              <img src="@/assets/icons/clock-black.svg" width="20" height="20" alt="clock" class="me-2" />
              <strong>{{ $t('Label Date Information') }}:</strong>
            </div>
            <p class="mb-0 mt-2">{{ prescription.labelExplanation }}</p>
          </div>
        </li>
      </ul>
    </div>
  </div>

  <Empty v-if="displayRules.showStoreRejectedModal" :size="'md'" :modal_id="'storeRejectedModal'" @hidden="hideStoreRejectedModal">
    <SpecialActions style="height: 400px" :title="$t('Select the reason for your request')"
      :placeholderNameSelect="$t('Select the reason')" :optionsSelect="prescriptionRejectedReasons" :notesMode="'disabled'"
      @selected="setMotivationIdSelected" @clear="clearRejectedInfo" @store="rejectPrescription(true)"
      @close="hideModal('storeRejectedModal');" />
  </Empty>
  <Standard
    v-if="showDateModal"
    modal_id="manualDateModal"
    size="md"
    :title="$t('The calculated date is in the past. Choose a new date/time:')"
    :decline_label="$t('Cancel')"
    :lightLayout="true"
    @close="showDateModal = false"
  >
    <div class="d-flex flex-column align-items-center">
      <p class="text-muted mb-3">{{ $t('Please select both date and time') }}</p>
      <DatePicker
        v-model="selectedDate"
        :min-date="new Date()"
        mode="dateTime"
        is24hr
        locale="it"
        :popover="false"
        :model-config="{ type: 'date' }"
      />
      <button
        class="btn btn-violet mt-3"
        :class="{ 'btn-secondary': !selectedDate }"
        :disabled="!selectedDate"
        @click="confirmDate"
      >
        {{ $t('Confirm') }}
      </button>
    </div>
  </Standard>
  <Standard
    v-if="showConfirmDateModal"
    modal_id="confirmDateModal"
    size="md"
    :title="$t('The calculated date is very close. Do you want to confirm this date/time?')"
    :decline_label="$t('Choose another date')"
    :lightLayout="true"
    @close="showConfirmDateModal = false"
    @decline="chooseAnotherDate"
  >
    <div class="d-flex flex-column align-items-center">
      <div class="mb-3">
        <strong>{{ pendingDateToConfirm ? pendingDateToConfirm.toLocaleString() : '' }}</strong>
      </div>
      <button class="btn btn-violet mb-2" @click="confirmPendingDate">{{ $t('Confirm this date') }}</button>
    </div>
  </Standard>
</template>

<style scoped>
#slot-details {
  position: absolute;
  width: 20%;
  left: 20%;
  top: 33%;
}

input[type="text"]:focus {
  border-color: rgb(225 210 242);
  box-shadow: 0 0 0 0.25rem rgb(225 210 242);
  outline: 0 none;
}

tr th {
  text-transform: uppercase;
}

.trash:hover {
  cursor: pointer;
  transform: scale(1.1);
}

.overflow-card {
  max-height: 65vh;
  overflow-y: scroll;
}

.not-focused {
  opacity: 0.5;
}

/* PRESCRIPTIONS */
.prescription {
  border: 1px solid;
  padding: 3px 6px;
  margin-bottom: 6px;
  border-radius: 0.5rem;
}
</style>
